import { Link, useLocation } from 'react-router-dom';
import { cn } from '../../lib/utils';
import {
  LayoutDashboard,
  TrendingUp,
  History,
  Radio,
  Shield,
  Bell,
  ClipboardList,
  BookOpen,
  Settings,
  Users,
  Cpu,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { useMode } from '../../hooks/useMode';
import { getLabel, type TerminologyKey } from '../../lib/terminology';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';
import { useState, useEffect } from 'react';

interface NavItem {
  labelKey: TerminologyKey;
  href: string;
  icon: React.ReactNode;
  adminOnly?: boolean;
}

const navItems: NavItem[] = [
  { labelKey: 'commandCenter', href: '/dashboard', icon: <LayoutDashboard className="w-5 h-5" /> },
  { labelKey: 'tradingViewSignals', href: '/signals', icon: <Radio className="w-5 h-5" /> },
  { labelKey: 'positions', href: '/positions', icon: <TrendingUp className="w-5 h-5" /> },
  { labelKey: 'reviewNav', href: '/trades', icon: <History className="w-5 h-5" /> },
  { labelKey: 'controlsNav', href: '/risk', icon: <Shield className="w-5 h-5" /> },
  { labelKey: 'mt5Status', href: '/mt5-status', icon: <Cpu className="w-5 h-5" /> },
  { labelKey: 'events', href: '/alerts', icon: <Bell className="w-5 h-5" /> },
  { labelKey: 'audit', href: '/audit', icon: <ClipboardList className="w-5 h-5" /> },
  { labelKey: 'howItWorks', href: '/how-it-works', icon: <BookOpen className="w-5 h-5" /> },
  { labelKey: 'settings', href: '/admin/users', icon: <Users className="w-5 h-5" />, adminOnly: true },
];

const USERS_LABEL = 'Users';

const settingsSubItems: { labelKey: TerminologyKey; href: string }[] = [
  { labelKey: 'settings', href: '/settings' },
  { labelKey: 'settingsHistoryNav', href: '/settings/history' },
];

interface SidebarNavContentProps {
  isAdmin?: boolean;
  className?: string;
  onLinkClick?: () => void;
}

export function SidebarNavContent({ isAdmin = false, className, onLinkClick }: SidebarNavContentProps) {
  const location = useLocation();
  const { mode } = useMode();
  const isSettingsActive = location.pathname.startsWith('/settings');
  const [settingsOpen, setSettingsOpen] = useState(isSettingsActive);

  useEffect(() => {
    if (isSettingsActive && !settingsOpen) setSettingsOpen(true);
  }, [isSettingsActive, settingsOpen]);

  const mainNavItems = navItems.filter((item) => !item.adminOnly);
  const adminItem = navItems.find((item) => item.adminOnly);

  return (
    <nav className={cn('flex flex-col gap-1', className)}>
      {mainNavItems.map((item) => (
        <Link
          key={item.href}
          to={item.href}
          onClick={onLinkClick}
          className={cn(
            'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
            item.href === '/dashboard'
              ? location.pathname === '/dashboard' || location.pathname === '/'
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              : location.pathname.startsWith(item.href)
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground'
          )}
        >
          {item.icon}
          {getLabel(item.labelKey, mode)}
        </Link>
      ))}

      <Collapsible
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        defaultOpen={isSettingsActive}
      >
        <CollapsibleTrigger
          className={cn(
            'flex w-full items-center justify-between gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
            isSettingsActive
              ? 'bg-primary text-primary-foreground'
              : 'text-muted-foreground hover:bg-muted hover:text-foreground'
          )}
        >
          <span className="flex items-center gap-3">
            <Settings className="w-5 h-5" />
            {getLabel('settings', mode)}
          </span>
          {settingsOpen ? (
            <ChevronDown className="h-4 w-4 shrink-0" />
          ) : (
            <ChevronRight className="h-4 w-4 shrink-0" />
          )}
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="ml-4 mt-1 flex flex-col gap-0.5 border-l border-border pl-3">
            {settingsSubItems.map((sub) => {
              const isSubActive =
                sub.href === '/settings'
                  ? location.pathname === '/settings'
                  : location.pathname === sub.href;
              return (
                <Link
                  key={sub.href}
                  to={sub.href}
                  onClick={onLinkClick}
                  className={cn(
                    'rounded-md px-2 py-1.5 text-sm transition-colors',
                    isSubActive
                      ? 'font-medium text-foreground'
                      : 'text-muted-foreground hover:text-foreground'
                  )}
                >
                  {getLabel(sub.labelKey, mode)}
                </Link>
              );
            })}
          </div>
        </CollapsibleContent>
      </Collapsible>

      {isAdmin && adminItem && (
        <Link
          to={adminItem.href}
          onClick={onLinkClick}
          className={cn(
            'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
            location.pathname.startsWith(adminItem.href)
              ? 'bg-primary text-primary-foreground'
              : 'text-muted-foreground hover:bg-muted hover:text-foreground'
          )}
        >
          {adminItem.icon}
          {USERS_LABEL}
        </Link>
      )}
    </nav>
  );
}
